/* assets/kit-add.js
   - Adiciona kits (múltiplas variantes) via /cart/add.js
   - Atualiza cart-drawer via /cart?sections=
   - Abre o drawer atualizado
   - Suporta:
     (A) data-kit-map  -> mapeia itens por variant do produto principal (recomendado p/ 50/60cm etc.)
     (B) data-kit-items -> lista fixa de itens do kit (fallback)
*/

(() => {
  // ========= Utils =========
  function safeJsonParse(str, fallback) {
    try {
      const v = JSON.parse(str);
      return v ?? fallback;
    } catch {
      return fallback;
    }
  }

  function toInt(n, d = NaN) {
    const x = Number(n);
    return Number.isFinite(x) ? x : d;
  }

  function clampQty(n) {
    const x = Math.max(1, Number(n || 1));
    return Number.isFinite(x) ? x : 1;
  }

  // ========= Variant resolution (robusto) =========
function getSelectedVariantId(form, btn) {
  // 1) input[name="id"] dentro do form deve ser prioridade
  const idInput = form?.querySelector('input[name="id"]:not([disabled])');
  const formId = idInput ? toInt(idInput.value) : NaN;

  if (Number.isFinite(formId) && formId > 0) {
    return formId;
  }

  // 2) fallback: URL param ?variant=
  const urlVar = new URL(window.location.href).searchParams.get("variant");
  const urlId = urlVar ? toInt(urlVar) : NaN;

  if (Number.isFinite(urlId) && urlId > 0) {
    return urlId;
  }

  // 3) fallback do botão
  const dataId = toInt(btn?.getAttribute("data-main-variant"));

  if (Number.isFinite(dataId) && dataId > 0) {
    return dataId;
  }

  return NaN;
}

  function getMainQty(form) {
    // tenta ler input quantity (se existir)
    const qtyInput =
      form?.querySelector('input[name="quantity"]:not([disabled])') ||
      form?.querySelector('[name="quantity"]:not([disabled])');
    return clampQty(qtyInput?.value || 1);
  }

  // ========= Cart API =========
  async function cartAdd(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });

    const text = await res.text();
    let json = null;
    try { json = JSON.parse(text); } catch {}

    if (!res.ok) {
      const msg = json?.description || json?.message || text;
      throw new Error(msg);
    }

    return json;
  }

  // ========= Drawer refresh via sections =========
  function getSectionIdFromElement(el) {
    const section = el?.closest?.('[id^="shopify-section-"]');
    if (!section?.id) return null;
    return section.id.replace("shopify-section-", "");
  }

  async function refreshCartDrawerSections() {
    const drawerEl =
      document.querySelector("cart-drawer") ||
      document.querySelector("#CartDrawer") ||
      document.querySelector(".cart-drawer") ||
      document.querySelector("[data-cart-drawer]");

    const bubbleEl =
      document.querySelector("#cart-icon-bubble") ||
      document.querySelector("cart-icon-bubble") ||
      document.querySelector("[data-cart-count]") ||
      document.querySelector(".header__cart-count");

    const ids = new Set();

    const drawerSectionId = getSectionIdFromElement(drawerEl);
    const bubbleSectionId = getSectionIdFromElement(bubbleEl);

    if (drawerSectionId) ids.add(drawerSectionId);
    if (bubbleSectionId) ids.add(bubbleSectionId);

    // fallbacks comuns (se existirem no tema, ótimo; se não, não atrapalha)
    ids.add("cart-drawer");
    ids.add("cart-icon-bubble");

    const sections = Array.from(ids).filter(Boolean).join(",");
    const url = `/cart?sections=${encodeURIComponent(sections)}`;

    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return;

    const data = await res.json();

    for (const [id, html] of Object.entries(data)) {
      const wrapper = document.getElementById(`shopify-section-${id}`);
      if (wrapper) wrapper.innerHTML = html;
    }

    // alguns temas precisam rebind depois de trocar HTML
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
  }

  // ========= Drawer open =========
  function openDrawer() {
    // web component
    const wd = document.querySelector("cart-drawer");
    if (wd && typeof wd.open === "function") {
      wd.open();
      return true;
    }

    // fallback DOM
    const drawer =
      document.querySelector("#CartDrawer") ||
      document.querySelector(".cart-drawer") ||
      document.querySelector("[data-cart-drawer]");

    if (drawer) {
      drawer.classList.add("is-open", "active", "open");
      drawer.setAttribute("open", "");
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");
      return true;
    }

    document.dispatchEvent(new CustomEvent("cart:open"));
    return false;
  }

  // ========= Build items =========
  function buildKitItems(btn, selectedMainVariantId) {
    // Preferido: map por variant do conjunto (resolve 50cm/60cm etc.)
    const mapStr = btn.getAttribute("data-kit-map");
    if (mapStr) {
      const kitMap = safeJsonParse(mapStr, {});
      const key = String(selectedMainVariantId);
      const list = kitMap?.[key];
      if (Array.isArray(list) && list.length) {
        return list
          .map((it) => ({ id: toInt(it?.id), quantity: clampQty(it?.qty || 1) }))
          .filter((it) => Number.isFinite(it.id) && it.id > 0);
      }
    }

    // Fallback: lista fixa
    const itemsStr = btn.getAttribute("data-kit-items") || "[]";
    const kitItems = safeJsonParse(itemsStr, []);
    if (Array.isArray(kitItems) && kitItems.length) {
      return kitItems
        .map((it) => ({ id: toInt(it?.id), quantity: clampQty(it?.qty || 1) }))
        .filter((it) => Number.isFinite(it.id) && it.id > 0);
    }

    return [];
  }

  // ========= Main click handler =========
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-kit-add]");
    if (!btn) return;

    const formId = btn.getAttribute("data-form-id");
    const form = formId ? document.getElementById(formId) : null;

    const selectedMainVariantId = getSelectedVariantId(form, btn);
    const includeMain = btn.getAttribute("data-kit-include-main") !== "0";
    const openOnSuccess = btn.getAttribute("data-kit-open-drawer") === "1";

    // Monta itens do kit (idealmente depende da variant selecionada)
    const kitItems = buildKitItems(btn, selectedMainVariantId);

    // Se for pra incluir main, valida e adiciona
    const items = [];
    if (includeMain) {
      if (!Number.isFinite(selectedMainVariantId) || selectedMainVariantId <= 0) {
        alert("Selecione uma variação válida antes de adicionar.");
        return;
      }
      items.push({ id: selectedMainVariantId, quantity: getMainQty(form) });
    }

    // Sempre precisa ter itens do kit
    if (!kitItems.length) {
      alert("Este conjunto não está configurado (nenhum item do kit encontrado).");
      return;
    }

    items.push(...kitItems);

    // Evita duplo clique
    const prevText = btn.textContent;
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");

    try {
      await cartAdd(items);

      // Atualiza drawer e contador (sem F5)
      await refreshCartDrawerSections();

      // Abre drawer já atualizado
      if (openOnSuccess) openDrawer();

    } catch (err) {
      console.error("[KIT] erro:", err);
      alert(`Não foi possível adicionar o conjunto.\n\n${err.message || "Verifique disponibilidade/variações."}`);
    } finally {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      if (prevText) btn.textContent = prevText;
    }
  });
})();